import React, { useState, useEffect } from 'react';
import { View, Text, Pressable, StyleSheet, Platform, ActivityIndicator, TextInput, Modal, TouchableWithoutFeedback, ScrollView } from 'react-native';
import { Plus, X, Edit2, Trash2, Check, MapPin } from '../lib/icons';
import { supabase } from '../lib/supabase';
import { shadows } from '../lib/styles';

interface Market {
  id: string;
  name: string;
  description: string | null;
  is_active: boolean;
  walkthrough_type: 'in_person' | 'virtual';
  is_default: boolean;
  created_at: string;
  updated_at: string;
}

interface MarketsManagerProps {
  onMarketSelect?: (marketId: string | null) => void;
  selectedMarketId?: string | null;
}

const MarketsManager: React.FC<MarketsManagerProps> = ({ onMarketSelect: _onMarketSelect, selectedMarketId: _selectedMarketId }) => {
  const [loading, setLoading] = useState(true);
  const [markets, setMarkets] = useState<Market[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [showModal, setShowModal] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    description: '',
    is_active: true,
    walkthrough_type: 'in_person' as 'in_person' | 'virtual',
    is_default: false,
  });
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetchMarkets();
  }, []);

  const fetchMarkets = async () => {
    try {
      setLoading(true);
      const { data, error: fetchError } = await supabase
        .from('markets')
        .select('*')
        .order('name', { ascending: true });

      if (fetchError) throw fetchError;
      setMarkets(data || []);
    } catch (err: any) {
      console.error('Error fetching markets:', err);
      setError(err.message || 'Failed to load markets');
    } finally {
      setLoading(false);
    }
  };

  const handleOpenModal = (market?: Market) => {
    if (market) {
      setEditingId(market.id);
      setFormData({
        name: market.name,
        description: market.description || '',
        is_active: market.is_active,
        walkthrough_type: market.walkthrough_type,
        is_default: market.is_default,
      });
    } else {
      setEditingId(null);
      setFormData({
        name: '',
        description: '',
        is_active: true,
        walkthrough_type: 'in_person',
        is_default: false,
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingId(null);
    setFormData({
      name: '',
      description: '',
      is_active: true,
      walkthrough_type: 'in_person',
      is_default: false,
    });
    setError(null);
  };

  const handleSave = async () => {
    if (!formData.name.trim()) {
      setError('Please enter a market name');
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const { data: { user } } = await supabase.auth.getUser();
      if (!user) throw new Error('Not authenticated');

      const marketData = {
        name: formData.name.trim(),
        description: formData.description.trim() || null,
        is_active: formData.is_active,
        walkthrough_type: formData.walkthrough_type,
        is_default: formData.is_default,
        created_by: editingId ? undefined : user.id,
      };

      if (editingId) {
        const { error: updateError } = await supabase
          .from('markets')
          .update(marketData)
          .eq('id', editingId);

        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('markets')
          .insert([marketData]);

        if (insertError) throw insertError;
      }

      await fetchMarkets();
      handleCloseModal();
    } catch (err: any) {
      console.error('Error saving market:', err);
      setError(err.message || 'Failed to save market');
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async (id: string) => {
    if (!confirm('Are you sure you want to delete this market? This will remove it from all availability entries.')) {
      return;
    }

    try {
      const { error: deleteError } = await supabase
        .from('markets')
        .delete()
        .eq('id', id);

      if (deleteError) throw deleteError;
      await fetchMarkets();
    } catch (err: any) {
      console.error('Error deleting market:', err);
      setError(err.message || 'Failed to delete market');
    }
  };

  const handleToggleActive = async (id: string, currentActive: boolean) => {
    try {
      const { error: updateError } = await supabase
        .from('markets')
        .update({ is_active: !currentActive })
        .eq('id', id);

      if (updateError) throw updateError;
      await fetchMarkets();
    } catch (err: any) {
      console.error('Error toggling active status:', err);
      setError(err.message || 'Failed to update market');
    }
  };

  if (loading) {
    return (
      <View style={styles.loadingContainer}>
        <ActivityIndicator size="large" color="#9ca3af" />
      </View>
    );
  }

  return (
    <View style={styles.container}>
      <View style={styles.header}>
        <View style={styles.headerLeft}>
          <MapPin size={24} color="#111827" />
          <Text style={styles.title}>Markets</Text>
        </View>
        <Pressable
          onPress={() => handleOpenModal()}
          style={styles.addButton}
        >
          <Plus size={16} color="#ffffff" />
          <Text style={styles.addButtonText}>Add Market</Text>
        </Pressable>
      </View>

      {error && (
        <View style={styles.errorContainer}>
          <Text style={styles.errorText}>{error}</Text>
        </View>
      )}

      {markets.length === 0 ? (
        <View style={styles.emptyContainer}>
          <MapPin size={48} color="#9ca3af" />
          <Text style={styles.emptyTitle}>No Markets</Text>
          <Text style={styles.emptyText}>
            Create your first market to organize walkthrough availability by geographic location.
          </Text>
        </View>
      ) : (
        <View style={styles.list}>
          {markets.map((market) => (
            <View key={market.id} style={styles.item}>
              <View style={styles.itemContent}>
                <View style={styles.itemHeader}>
                  <View style={styles.itemHeaderLeft}>
                    <MapPin size={20} color={market.is_active ? "#2563eb" : "#9ca3af"} />
                    <View>
                      <Text style={[
                        styles.itemName,
                        !market.is_active && styles.itemNameInactive
                      ]}>
                        {market.name}
                      </Text>
                      {market.description && (
                        <Text style={styles.itemDescription}>
                          {market.description}
                        </Text>
                      )}
                      <View style={styles.marketMeta}>
                        <Text style={styles.marketType}>
                          {market.walkthrough_type === 'virtual' ? '📹 Virtual' : '🏠 In-Person'}
                        </Text>
                      </View>
                    </View>
                  </View>
                  <View style={styles.itemBadges}>
                    {market.is_default && (
                      <View style={styles.defaultBadge}>
                        <Text style={styles.defaultBadgeText}>Default</Text>
                      </View>
                    )}
                    {market.is_active ? (
                      <View style={styles.activeBadge}>
                        <Text style={styles.activeBadgeText}>Active</Text>
                      </View>
                    ) : (
                      <View style={styles.inactiveBadge}>
                        <Text style={styles.inactiveBadgeText}>Inactive</Text>
                      </View>
                    )}
                  </View>
                </View>
              </View>
              <View style={styles.itemActions}>
                <Pressable
                  onPress={() => handleToggleActive(market.id, market.is_active)}
                  style={styles.actionButton}
                >
                  {market.is_active ? (
                    <X size={18} color="#dc2626" />
                  ) : (
                    <Check size={18} color="#16a34a" />
                  )}
                  <Text style={[
                    styles.actionButtonText,
                    market.is_active ? styles.deactivateText : styles.activateText
                  ]}>
                    {market.is_active ? 'Deactivate' : 'Activate'}
                  </Text>
                </Pressable>
                <Pressable
                  onPress={() => handleOpenModal(market)}
                  style={styles.actionButton}
                >
                  <Edit2 size={18} color="#2563eb" />
                  <Text style={[styles.actionButtonText, styles.editText]}>Edit</Text>
                </Pressable>
                <Pressable
                  onPress={() => handleDelete(market.id)}
                  style={styles.actionButton}
                >
                  <Trash2 size={18} color="#dc2626" />
                  <Text style={[styles.actionButtonText, styles.deleteText]}>Delete</Text>
                </Pressable>
              </View>
            </View>
          ))}
        </View>
      )}

      {/* Add/Edit Market Modal */}
      <Modal
        visible={showModal}
        transparent
        animationType="slide"
        onRequestClose={handleCloseModal}
      >
        <TouchableWithoutFeedback onPress={handleCloseModal}>
          <View style={styles.modalOverlay}>
            <TouchableWithoutFeedback>
              <ScrollView style={styles.modalContent} contentContainerStyle={styles.modalContentContainer}>
                <View style={styles.modalHeader}>
                  <Text style={styles.modalTitle}>
                    {editingId ? 'Edit Market' : 'Add Market'}
                  </Text>
                  <Pressable onPress={handleCloseModal}>
                    <X size={24} color="#6b7280" />
                  </Pressable>
                </View>

                <View style={styles.form}>
                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Market Name *</Text>
                    <TextInput
                      style={styles.input}
                      value={formData.name}
                      onChangeText={(text) => setFormData({ ...formData, name: text })}
                      placeholder="e.g., Grand Rapids"
                      placeholderTextColor="#9ca3af"
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Description</Text>
                    <TextInput
                      style={styles.textArea}
                      value={formData.description}
                      onChangeText={(text) => setFormData({ ...formData, description: text })}
                      placeholder="Optional description of this market"
                      placeholderTextColor="#9ca3af"
                      multiline
                      numberOfLines={3}
                    />
                  </View>

                  <View style={styles.formGroup}>
                    <Text style={styles.label}>Walkthrough Type</Text>
                    {Platform.OS === 'web' ? (
                      <select
                        value={formData.walkthrough_type}
                        onChange={(e) => setFormData({ ...formData, walkthrough_type: e.target.value as 'in_person' | 'virtual' })}
                        style={{
                          marginTop: 8,
                          padding: '8px 12px',
                          border: '1px solid #d1d5db',
                          borderRadius: 8,
                          fontSize: 16,
                          backgroundColor: '#ffffff',
                        }}
                      >
                        <option value="in_person">In-Person</option>
                        <option value="virtual">Virtual</option>
                      </select>
                    ) : (
                      <View style={styles.pickerContainer}>
                        <TextInput
                          style={styles.input}
                          value={formData.walkthrough_type === 'virtual' ? 'Virtual' : 'In-Person'}
                          editable={false}
                          placeholder="Select walkthrough type"
                          placeholderTextColor="#9ca3af"
                        />
                      </View>
                    )}
                    <Text style={styles.helpText}>
                      {formData.walkthrough_type === 'virtual' 
                        ? 'Virtual walkthroughs are conducted via video call' 
                        : 'In-person walkthroughs require a physical visit to the home'}
                    </Text>
                  </View>

                  <View style={styles.formGroup}>
                    <Pressable
                      onPress={() => setFormData({ ...formData, is_default: !formData.is_default })}
                      style={styles.checkboxContainer}
                    >
                      <View style={[
                        styles.checkbox,
                        formData.is_default && styles.checkboxChecked
                      ]}>
                        {formData.is_default && <Check size={16} color="#ffffff" />}
                      </View>
                      <View style={styles.checkboxLabelContainer}>
                        <Text style={styles.checkboxLabel}>Set as Default Market</Text>
                        <Text style={styles.checkboxHelpText}>
                          Homes without a market assignment will use this market
                        </Text>
                      </View>
                    </Pressable>
                  </View>

                  <View style={styles.formGroup}>
                    <Pressable
                      onPress={() => setFormData({ ...formData, is_active: !formData.is_active })}
                      style={styles.checkboxContainer}
                    >
                      <View style={[
                        styles.checkbox,
                        formData.is_active && styles.checkboxChecked
                      ]}>
                        {formData.is_active && <Check size={16} color="#ffffff" />}
                      </View>
                      <Text style={styles.checkboxLabel}>Active</Text>
                    </Pressable>
                  </View>

                  {error && (
                    <View style={styles.errorContainer}>
                      <Text style={styles.errorText}>{error}</Text>
                    </View>
                  )}

                  <View style={styles.modalActions}>
                    <Pressable
                      onPress={handleCloseModal}
                      style={styles.cancelButton}
                    >
                      <Text style={styles.cancelButtonText}>Cancel</Text>
                    </Pressable>
                    <Pressable
                      onPress={handleSave}
                      disabled={saving}
                      style={[styles.saveButton, saving && styles.saveButtonDisabled]}
                    >
                      {saving ? (
                        <ActivityIndicator size="small" color="#ffffff" />
                      ) : (
                        <Text style={styles.saveButtonText}>Save</Text>
                      )}
                    </Pressable>
                  </View>
                </View>
              </ScrollView>
            </TouchableWithoutFeedback>
          </View>
        </TouchableWithoutFeedback>
      </Modal>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#ffffff',
    padding: 16,
    gap: 16,
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  headerLeft: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    color: '#111827',
  },
  addButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    backgroundColor: '#000000',
    borderRadius: 8,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  addButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  loadingContainer: {
    flex: 1,
    justifyContent: 'center',
    alignItems: 'center',
    padding: 40,
  },
  errorContainer: {
    padding: 12,
    backgroundColor: '#fef2f2',
    borderRadius: 8,
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  errorText: {
    color: '#dc2626',
    fontSize: 14,
  },
  emptyContainer: {
    padding: 60,
    alignItems: 'center',
    gap: 16,
  },
  emptyTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#111827',
  },
  emptyText: {
    fontSize: 16,
    color: '#6b7280',
    textAlign: 'center',
  },
  list: {
    gap: 12,
  },
  item: {
    backgroundColor: '#f9fafb',
    borderRadius: 12,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    padding: 16,
    gap: 12,
    ...(Platform.OS === 'web' && {
      ...shadows.sm,
    }),
  },
  itemContent: {
    gap: 12,
  },
  itemHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
  },
  itemHeaderLeft: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    gap: 12,
    flex: 1,
  },
  itemName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#111827',
  },
  itemNameInactive: {
    color: '#9ca3af',
  },
  itemDescription: {
    fontSize: 14,
    color: '#6b7280',
    marginTop: 4,
  },
  marketMeta: {
    marginTop: 6,
    flexDirection: 'row',
    gap: 8,
  },
  marketType: {
    fontSize: 12,
    color: '#6b7280',
    fontWeight: '500',
  },
  itemBadges: {
    gap: 8,
  },
  defaultBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#dbeafe',
    borderRadius: 12,
  },
  defaultBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#2563eb',
  },
  activeBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#dcfce7',
    borderRadius: 12,
  },
  activeBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#16a34a',
  },
  inactiveBadge: {
    paddingHorizontal: 8,
    paddingVertical: 4,
    backgroundColor: '#f3f4f6',
    borderRadius: 12,
  },
  inactiveBadgeText: {
    fontSize: 12,
    fontWeight: '600',
    color: '#6b7280',
  },
  itemActions: {
    flexDirection: 'row',
    gap: 12,
    paddingTop: 12,
    borderTopWidth: 1,
    borderTopColor: '#e5e7eb',
  },
  actionButton: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 6,
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  actionButtonText: {
    fontSize: 14,
    fontWeight: '500',
  },
  editText: {
    color: '#2563eb',
  },
  deleteText: {
    color: '#dc2626',
  },
  activateText: {
    color: '#16a34a',
  },
  deactivateText: {
    color: '#dc2626',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
    ...(Platform.OS === 'web' && {
      justifyContent: 'center',
      alignItems: 'center',
    }),
  },
  modalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: '90%',
    width: '100%',
    ...(Platform.OS === 'web' && {
      maxWidth: 600,
      minWidth: 400,
      width: 'auto',
      marginHorizontal: 'auto',
      borderRadius: 12,
      marginBottom: 20,
      marginTop: 20,
    }),
  },
  modalContentContainer: {
    padding: 24,
  },
  modalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 24,
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#111827',
  },
  form: {
    gap: 20,
  },
  formGroup: {
    gap: 8,
  },
  label: {
    fontSize: 14,
    fontWeight: '600',
    color: '#374151',
  },
  input: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
  },
  textArea: {
    paddingHorizontal: 12,
    paddingVertical: 10,
    borderWidth: 1,
    borderColor: '#d1d5db',
    borderRadius: 8,
    fontSize: 16,
    color: '#111827',
    backgroundColor: '#ffffff',
    minHeight: 80,
    textAlignVertical: 'top',
  },
  checkboxContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 12,
  },
  checkbox: {
    width: 20,
    height: 20,
    borderWidth: 2,
    borderColor: '#d1d5db',
    borderRadius: 4,
    justifyContent: 'center',
    alignItems: 'center',
  },
  checkboxChecked: {
    backgroundColor: '#000000',
    borderColor: '#000000',
  },
  checkboxLabel: {
    fontSize: 14,
    color: '#374151',
  },
  checkboxLabelContainer: {
    flex: 1,
  },
  checkboxHelpText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 2,
  },
  helpText: {
    fontSize: 12,
    color: '#6b7280',
    marginTop: 4,
  },
  pickerContainer: {
    marginTop: 8,
  },
  modalActions: {
    flexDirection: 'row',
    gap: 12,
    marginTop: 8,
  },
  cancelButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#f3f4f6',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  cancelButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#374151',
  },
  saveButton: {
    flex: 1,
    paddingVertical: 12,
    paddingHorizontal: 24,
    backgroundColor: '#000000',
    borderRadius: 8,
    alignItems: 'center',
    ...(Platform.OS === 'web' && {
      cursor: 'pointer',
    }),
  },
  saveButtonDisabled: {
    opacity: 0.5,
  },
  saveButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
});

export default MarketsManager;

